import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CustomerChrome } from '#bv/components/customer/CustomerChrome'
import { getSupabaseClient } from '#bv/lib/supabaseClient'
import { OrderStatusPill } from '#bv/components/customer/OrderStatusPill'

const searchSchema = z.object({
  orderId: z.string().optional(),
  email: z.string().optional(),
})

export const Route = createFileRoute('/customer/orders')({
  validateSearch: searchSchema,
  component: CustomerOrdersPage,
})

type StatusRow =
  | { loading: true }
  | {
      loading: false
      ok: true
      email: string
      orders: Array<{
        channel: 'moneymotion' | 'paypal_ff' | 'crypto'
        orderId: string
        status: 'pending' | 'paid' | 'cancelled' | 'refunded'
        readyForDelivery: boolean
        productName: string
        amountUsd: number
        createdAt: string
        updatedAt?: string
      }>
    }
  | { loading: false; ok: false; error: string; status?: number }

function ChannelBadge({ channel }: { channel: string }) {
  const cfg = channel === 'crypto'
    ? { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.15)', color: '#fbbf24', label: 'Crypto', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg> }
    : channel === 'paypal_ff'
    ? { bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.15)', color: '#60a5fa', label: 'PayPal', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg> }
    : { bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.15)', color: '#a78bfa', label: 'Card', icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg> }

  return (
    <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[0.6rem] font-semibold" style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color }}>
      {cfg.icon}{cfg.label}
    </span>
  )
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-white/[0.06] p-4 sm:p-5" style={{ background: 'rgba(10,8,16,0.6)' }}>
      <div className="flex justify-between">
        <div className="space-y-2.5">
          <div className="h-3 w-40 rounded bg-white/[0.04]" style={{ animation: 'pulse 2s ease infinite' }} />
          <div className="h-4 w-56 rounded bg-white/[0.06]" style={{ animation: 'pulse 2s ease infinite', animationDelay: '0.1s' }} />
          <div className="h-3 w-32 rounded bg-white/[0.04]" style={{ animation: 'pulse 2s ease infinite', animationDelay: '0.2s' }} />
        </div>
        <div className="h-7 w-16 rounded-lg bg-white/[0.04]" style={{ animation: 'pulse 2s ease infinite' }} />
      </div>
    </div>
  )
}

function CustomerOrdersPage() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [status, setStatus] = useState<StatusRow>({ loading: true })

  const refresh = useCallback(async () => {
    setStatus({ loading: true })
    try {
      const supabase = getSupabaseClient()
      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      if (!token) { setStatus({ loading: false, ok: false, error: 'unauthorized', status: 401 }); return }
      const res = await fetch('/api/customer-orders', { method: 'GET', headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } })
      const j = (await res.json()) as any
      if (!res.ok) { setStatus({ loading: false, ok: false, error: typeof j?.error === 'string' ? j.error : `http_${res.status}`, status: res.status }); return }
      setStatus({ loading: false, ok: true, email: j.email, orders: j.orders ?? [] })
    } catch (e) {
      setStatus({ loading: false, ok: false, error: e instanceof Error ? e.message : 'Request failed' })
    }
  }, [])

  useEffect(() => {
    if (!search.orderId && !search.email) return
    void navigate({ to: '/customer/orders', search: {}, replace: true })
  }, [navigate, search.email, search.orderId])

  useEffect(() => { void refresh() }, [refresh])

  const orderCount = !status.loading && status.ok ? status.orders.length : 0

  return (
    <CustomerChrome active="orders" title="Orders" subtitle="Status, delivery readiness, and support access.">
      <style dangerouslySetInnerHTML={{ __html: '@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}' }} />

      <div className="rounded-2xl border border-white/[0.06] overflow-hidden" style={{ background: 'rgba(10,8,16,0.8)' }}>
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] px-5 py-4" style={{ background: 'rgba(0,0,0,0.2)' }}>
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'rgba(0, 191, 255,0.08)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
            </div>
            <div>
              <h2 className="m-0 text-sm font-extrabold tracking-tight text-[#f0f6ff]">Your orders</h2>
              <p className="m-0 text-[0.6rem] text-[#5a6d8a]">
                {status.loading ? 'Loading...' : !status.ok ? '—' : `${orderCount} order${orderCount !== 1 ? 's' : ''} found`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={status.loading}
            className="inline-flex items-center gap-1.5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-2 text-xs font-semibold text-[#7a92b8] transition-colors hover:text-[#f0f6ff] hover:border-[rgba(0, 191, 255,0.15)] disabled:opacity-50"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={status.loading ? 'animate-spin' : ''}><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" /></svg>
            Refresh
          </button>
        </header>

        <div className="p-5 sm:p-6">
          {status.loading ? (
            <div className="space-y-3">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : !status.ok ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed border-white/[0.06] py-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full" style={{ background: 'rgba(239,68,68,0.06)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              </div>
              <p className="m-0 mt-3 text-sm font-semibold text-[#f0f6ff]">Couldn't load orders</p>
              <p className="m-0 mt-1 text-xs text-[#5a6d8a]">{status.error}</p>
            </div>
          ) : status.orders.length === 0 ? (
            <div className="flex flex-col items-center rounded-xl border border-dashed border-white/[0.06] py-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full" style={{ background: 'rgba(0, 191, 255,0.06)' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5a6d8a" strokeWidth="1.5"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
              </div>
              <p className="m-0 mt-4 text-sm font-semibold text-[#f0f6ff]">No orders found yet</p>
              <p className="m-0 mt-1 text-xs text-[#5a6d8a] max-w-[300px]">After checkout, orders may take a moment to appear while webhooks process.</p>
              <Link to="/shop" className="btn-primary mt-5 rounded-xl px-6 py-2.5 text-sm font-semibold">Browse products</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {status.orders.map((o) => (
                <article key={`${o.channel}:${o.orderId}:${o.createdAt}`} className="group rounded-2xl border border-white/[0.06] p-4 sm:p-5 transition-all duration-200 hover:border-[rgba(0, 191, 255,0.12)] hover:bg-white/[0.01]" style={{ background: 'rgba(0,0,0,0.15)' }}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="m-0 font-mono text-xs text-[#8ecfff]/90">{o.orderId}</p>
                        <ChannelBadge channel={o.channel} />
                      </div>
                      <p className="m-0 mt-2 text-sm font-semibold text-[#f0f6ff]">{o.productName ?? '—'}</p>
                      <div className="mt-1.5 flex items-center gap-2 text-xs text-[#5a6d8a]">
                        <span className="font-semibold tabular-nums text-[#5a6d8a]">${Number(o.amountUsd ?? 0).toFixed(2)}</span>
                        <span>·</span>
                        <span>{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '—'}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <OrderStatusPill status={o.status} readyForDelivery={o.readyForDelivery} />
                      <Link to="/customer/orders/$orderId" params={{ orderId: o.orderId }} className="inline-flex items-center gap-1 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-2 text-xs font-semibold text-[#7a92b8] transition-all hover:text-[#f0f6ff] hover:border-[rgba(0, 191, 255,0.15)]">
                        View
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-white/[0.06] p-5 sm:p-6" style={{ background: 'rgba(10,8,16,0.6)' }}>
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'rgba(245,158,11,0.08)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          </div>
          <div>
            <p className="m-0 text-sm font-semibold text-[#f0f6ff]">Missing an order?</p>
            <p className="m-0 mt-1 text-xs text-[#5a6d8a]">For privacy, we only show orders matching your checkout email. Recent payments may take a short time for the webhook to process.</p>
          </div>
        </div>
      </div>
    </CustomerChrome>
  )
}
